import { ReactNode, memo, useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"

import styled, { css } from "styled-components"

import { borderRadius, colors, spacing, typography, size, elevation } from "@izettle/otto"

import { useOnResize } from "../../../hooks/use-on-resize"
import { useStateRef } from "../../../utils/state-ref"
import {
  ZINDEX_SINGLE_SELECT_POP_OVER,
  ZINDEX_SINGLE_SELECT_POP_OVER_FULLSCREEN,
} from "../helpers/constants"

const mixinIsExpanded = css<{ isExpanded?: boolean }>`
  ${({ isExpanded }) =>
    isExpanded
      ? `
      opacity: 1;
      visibility: visible;
    `
      : `
      opacity: 0;
      transform: translateY(${spacing.xxSmall});
      pointer-events: none;
      visibility: hidden;
    `}
`

const PopOverPlacement = memo(styled.div<{ left: number }>`
  position: relative;
  width: 100%;
  z-index: ${ZINDEX_SINGLE_SELECT_POP_OVER};
`)

/* Here z-index is set to 20, to be above the FloatingLabels of adjacent components */
const PopOverContainer = memo(styled.div<{
  isExpanded?: boolean
  isLoading?: boolean
  hasActions?: boolean
  left: number
}>`
  ${({ left }) => {
    return `
      left: ${left}px;
    `
  }}
  ${typography.regular}
  margin: 0;
  width: fit-content;
  overflow-y: scroll;
  background: ${colors.background.default};
  cursor: default;
  display: flex;
  flex-direction: column;
  padding: ${spacing.medium};
  border-radius: ${borderRadius.large};
  box-shadow: ${elevation.level3};
  transition:
    opacity 0.2s,
    transform 0.2s;
  min-height: ${size.large};
  position: absolute;
  /* The border is always in focus state when open. */
  ${mixinIsExpanded}
  ${({ isLoading }) =>
    isLoading &&
    `
      cursor: progress;
    `}
  ${({ hasActions }) =>
    hasActions &&
    `
      padding-bottom: 0;
    `}
`)

const PopOverMobile = memo(styled.div<{ isExpanded?: boolean }>`
  position: fixed;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${colors.background.default};
  transition:
    opacity 0.2s,
    transform 0.2s;
  display: flex;
  flex-direction: column;
  padding: ${spacing.xSmall} 0 0 0;
  z-index: ${ZINDEX_SINGLE_SELECT_POP_OVER_FULLSCREEN};
  align-self: flex-end;
  ${mixinIsExpanded}
`)

type PopOverProps = {
  children?: ReactNode
  isExpanded: boolean
  isMobile: boolean
  onRefList?: (refInputBase: HTMLDivElement | null) => void
}

export const PopOver = ({ children, isExpanded, isMobile, onRefList }: PopOverProps) => {
  const [refListPlacement, setRefListPlacement] = useStateRef<HTMLDivElement | null>(
    (node) => node,
  )
  const [refListContainer, setRefListContainer] = useStateRef<HTMLDivElement | null>(
    (node) => node,
  )
  const [shift, setShift] = useState(0)

  const handleShift = useCallback(() => {
    const bbox = refListContainer?.getBoundingClientRect() ?? null
    const left = bbox?.left ?? 0
    const width = (bbox && bbox.right - bbox.left) || 0
    const screenWidth = window.innerWidth
    const overflow = screenWidth - left - width
    const offset = overflow < 0 ? overflow : 0
    setShift(offset)
  }, [refListContainer])

  useEffect(() => {
    if (isMobile) {
      onRefList && refListPlacement && onRefList(refListPlacement)
    } else {
      onRefList && refListContainer && onRefList(refListContainer)
    }
  }, [isMobile, refListPlacement, refListContainer])

  useOnResize(() => {
    if (!isMobile) {
      handleShift()
    }
  }, [isMobile, refListContainer])

  useEffect(() => {
    if (!isMobile) {
      handleShift()
    }
  }, [isMobile, refListContainer])

  return isMobile ? (
    createPortal(
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
      <PopOverMobile isExpanded={isExpanded} ref={setRefListPlacement}>
        {children}
      </PopOverMobile>,
      document.body,
    )
  ) : (
    <PopOverPlacement ref={setRefListPlacement}>
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
      <PopOverContainer left={shift} ref={setRefListContainer} isExpanded={isExpanded}>
        {children}
      </PopOverContainer>
    </PopOverPlacement>
  )
}
